import classnames from 'classnames';
import React, { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react';

import { ErrorMessages } from 'common/components/Form/ErrorMessages';
import withFormState, {
  WithFormStateProps
} from 'common/components/Form/withFormState';
import Checkbox from 'common/components/SignUpForm/Checkbox';
import {
  USERSPACE_EMAIL_REGEXP,
  USERSPACE_NICKNAME_REGEXP
} from 'common/configuration/constants';
import { isRequired, isTrue, matchRegex } from 'common/forms/validators';
import { path } from 'common/tools/network/routing';
import trans from 'common/tools/translations/trans';
import { isString } from 'common/types';

import Input from '../Input';
import styles from './styles.module.scss';

type Props = {
  displayEmail?: boolean;
  email: string;
  errorMessages: string[];
  nickname: string | null;
  onReturn: () => void;
  onSubmit: (email: string, nickname: string, termsOfService?: boolean) => void;
  submitting: boolean;
};

type TermsOfServiceFormFields = 'email' | 'nickname' | 'termsOfService';

/**
 * Component that handle second step of sign up form
 *
 * Takes care of data validation
 *
 * Validation is done on focus out and on form submission
 */
const TermsOfServiceForm = ({
  displayEmail,
  email,
  errorMessages,
  nickname,
  fieldsState,
  formErrors,
  formValues,
  isFormValidating,
  onFieldBlur,
  onFieldChange,
  onReturn,
  onSubmit,
  submitting,
  validateForm
}: WithFormStateProps<TermsOfServiceFormFields> & Props) => {
  const [checked, setChecked] = useState(!!formValues.termsOfService);
  const [nicknameValue, setNicknameValue] = useState(formValues.nickname);

  useEffect(() => onFieldChange('termsOfService', checked), [checked]);
  useEffect(() => onFieldChange('nickname', nicknameValue), [nicknameValue]);
  useEffect(() => {
    onFieldChange('email', email);
    setNicknameValue(nickname ?? undefined);
  }, [email, nickname]);

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (
      isValid &&
      isString(formValues?.email) &&
      isString(formValues?.nickname)
    ) {
      onSubmit(
        formValues.email,
        formValues.nickname,
        formValues.termsOfService
      );
    }
  };

  const loading = isFormValidating || submitting;

  return (
    <>
      <h1 className="title">{trans('userspace.sign-up.title2')}</h1>
      <p className="paragraph">{trans('userspace.sign-up.subtitle2')}</p>
      <form onSubmit={handleSubmit} noValidate>
        {displayEmail && (
          <Input
            containerClassName={styles.fieldset}
            disabled
            label={trans('userspace.sign-up.placeholder.username')}
            name="email"
            type="text"
            value={formValues.email}
          />
        )}
        <Input
          containerClassName={styles.fieldset}
          errorClassName="input-error-message"
          errors={formErrors.nickname}
          helpText={trans('userspace.sign-up.hint.nickname')}
          label={trans('userspace.sign-up.placeholder.nickname')}
          name="nickname"
          onBlur={onFieldBlur.bind(null, 'nickname')}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setNicknameValue(e.target.value)
          }
          placeholder={trans('userspace.sign-up.placeholder.nickname')}
          type="text"
          validated={fieldsState.nickname?.validated}
          validating={fieldsState.nickname?.validating}
          value={nicknameValue}
        />
        <Checkbox
          errors={formErrors.termsOfService}
          label={trans('userspace.sign-up.conditions-of-use', {
            conditionsOfUse: path('cgu')
          })}
          name="termsOfService"
          onChange={() => setChecked(!checked)}
          value={checked}
        />
        <ErrorMessages messages={errorMessages} />
        {loading ? (
          <div className="button button-loading">...</div>
        ) : (
          <button
            type="submit"
            className="form-input button button-md button-inverse-full submit-button"
          >
            {trans('userspace.sign-up.go')}
          </button>
        )}
      </form>
      <a
        className={classnames(
          styles.previousStepLink,
          'icon',
          'icon-arrow-left'
        )}
        onClick={onReturn}
      >
        {trans('userspace.sign-up.previous-step')}
      </a>
    </>
  );
};

export default withFormState<Props, TermsOfServiceFormFields>({
  fields: ['email', 'nickname', 'termsOfService'],
  defaultValues: { nickname: '', termsOfService: false },
  validators: {
    email: [
      isRequired(trans('error.isrequired')),
      matchRegex(
        new RegExp(USERSPACE_EMAIL_REGEXP),
        trans('error.invalid-email')
      )
    ],
    nickname: [
      isRequired(trans('error.isrequired')),
      matchRegex(
        new RegExp(USERSPACE_NICKNAME_REGEXP),
        trans('error.pseudo-too-short')
      )
    ],
    termsOfService: [isTrue(trans('error.conditions-of-use'))]
  }
})(TermsOfServiceForm);
